/** LIFF-00 / SURFACE-LIFF contracts — FE types only; server remains source of truth. */

export type ObjectRef = {
  kind: string;
  id: string;
};

export type ActionDirective = {
  actionId: string;
  actionType: string;
  objectRef: ObjectRef;
  requiredRevision: number;
  requiredCapabilities: string[];
  authorityRevision?: number;
  riskTier: "low" | "medium" | "high";
  requiredConfirmation: "none" | "single_tap" | "impact_preview" | "explicit_text";
  operationTarget: string;
  operationKey: string;
  expiresAt?: string;
  staleBehavior: "refresh" | "deny" | "regenerate_preview";
  successProjectionRefs: ObjectRef[];
  errorProjection: "inline" | "refresh_card" | "open_recovery";
  /** Display label from server/projection — never invent operation semantics client-side. */
  label?: string;
  /** Open ยอดค้าง drawer on งานเงิน, or รายรับ on ข้อมูล. */
  dataNav?: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
    /** Scroll/focus this drawer field after open (e.g. customer_name). */
    focusField?: string;
    /** Open this tab inside the ข้อมูล record modal. */
    drawerTab?: "details" | "allocation" | "history";
  };
};

export type WorkBucketId =
  | "needs_action"
  | "waiting"
  | "needs_review"
  | "risk"
  | "recently_closed";

export type WorkPlacementV1 = {
  bucket: WorkBucketId;
  rank?: number;
  secondaryLabel?: string;
};

export type RequirementStatus = {
  complete: boolean;
  missingLabels?: string[];
  summary?: string;
};

export type NextStep = {
  label: string;
  assignedToCurrentActor?: boolean;
};

export type LiffSurfaceState =
  | "needs_action"
  | "waiting"
  | "needs_review"
  | "risk"
  | "closed"
  | "corrected"
  | "processing";

export type EvidenceItemStatus = "complete" | "missing" | "needs_review";

export type EvidenceItem = {
  id: string;
  label: string;
  status: EvidenceItemStatus;
  detail?: string;
  /** Same-origin LIFF image proxy — never a LINE/Drive token URL. */
  imageUrl?: string;
  dataNav?: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  };
  /** Receivable this slip closed — one evidence row per person. */
  matchedObjectId?: string;
  /** Closed receivable shown next to the slip, e.g. ยอดชีส. */
  counterpartLabel?: string;
  counterpartDetail?: string;
  /** Slip/payment time from BillLin — ISO, never inferred in LIFF. */
  occurredAt?: string;
  /** When this evidence was allocated to the receivable. */
  matchedAt?: string;
  /** Expiry/deadline from the source record. */
  expiresAt?: string;
  /** Where the slip arrived from — server-authored, e.g. LINE OA. */
  sourceLabel?: string;
  /** Transferor shown on unmatched slip evidence. */
  transferorLabel?: string;
  /** Formatted transfer amount retained when a matched pair is split into timeline rows. */
  transferAmountLabel?: string;
  /** Formatted allocation amount retained for already-flat timeline rows. */
  matchedAmountLabel?: string;
  /** Receivable gross at match time — server-authored. */
  originalAmountMinor?: number;
  /** Outstanding immediately before this allocation — server-authored. */
  outstandingAmountMinor?: number;
  /** Amount applied from this slip — server-authored. */
  matchedAmountMinor?: number;
  /** Full amount shown on the slip before allocation. */
  transferAmountMinor?: number;
  /** Receivable remaining after this match — server-authored. */
  remainingAmountMinor?: number;
};

export type WorkTimelineEvent = {
  id: string;
  label: string;
  occurredAtLabel: string;
};

export type LiffFinancialWorkCard = {
  objectRef: ObjectRef;
  caseRef?: ObjectRef;
  projectionRevision: number;
  audience: "person" | "scope_member" | "external_recipient" | "accountant";
  scopeRef?: ObjectRef;
  title: string;
  subtitle?: string;
  amountSummary?: {
    currency: string;
    primaryAmountMinor: number;
    basis: "cash" | "gross" | "remaining" | "due" | "aggregate";
    breakdown?: Array<{ label: string; amountMinor: number }>;
  };
  surfaceState: LiffSurfaceState;
  workPlacement: WorkPlacementV1;
  requirementStatus: RequirementStatus;
  nextStep: NextStep;
  assignedActorRef?: ObjectRef;
  contextRows: Array<{
    key: string;
    label: string;
    value: string | null;
    provenanceLabel?: string;
  }>;
  consequenceRows: Array<{
    objectRef: ObjectRef;
    label: string;
    status: string;
    amountMinor?: number;
    currency?: string;
    occurredAt?: string;
    expiresAt?: string;
  }>;
  evidenceSummary: { complete: number; missing: number; needsReview: number };
  /** Optional itemized evidence for the detail shell — server-authored only. */
  evidenceItems?: EvidenceItem[];
  /** R0-07 backlog children under an aggregate/batch card — server projection only. */
  backlogChildren?: BacklogChildRow[];
  /** R0-05 outstanding slice — open children + optional reminder drafts (never auto-sent). */
  outstanding?: OutstandingSlice;
  timelineSummary?: { latestEvent: string; occurredAt: string };
  /** Server- or fixture-authored events — FE must not infer money history. */
  timelineEvents?: WorkTimelineEvent[];
  availableActions: ActionDirective[];
  detailLink: string;
  freshness: "current" | "stale" | "projection_pending";
};

export type BacklogChildStatus =
  | "open"
  | "waiting_slip"
  | "paid"
  | "needs_review";

export type BacklogChildRow = {
  objectRef: ObjectRef;
  label: string;
  status: BacklogChildStatus;
  remainingAmountMinor?: number;
  /** Server-authored amount already applied — used for paid rows, never inferred. */
  paidAmountMinor?: number;
  /** Receivable gross — server-authored, never inferred as remaining+paid. */
  originalAmountMinor?: number;
  /** Announcement/slip time when the projection includes it. */
  occurredAt?: string;
  /** Expiry/deadline of this receivable. */
  expiresAt?: string;
  currency?: string;
  slipLabel?: string;
  /** Transferor on the matched slip — never the receivable customer name. */
  slipPartyName?: string;
  /** Same-origin LIFF image proxy when this child already has a matched รายรับ slip. */
  slipImageUrl?: string;
  detailLink?: string;
  dataNav?: {
    workerId: string;
    recordId?: string;
    scrollTo?: string;
    highlight?: string;
  };
};

export type OutstandingReminderDraft = {
  recipientLabel: string;
  objectRef: ObjectRef;
  /** Server- or fixture-authored copy — FE may display/copy only unless sendApproved. */
  messageDraft: string;
  /** LINE direct-tone draft. When omitted, UI shows only `messageDraft`. */
  messageDraftDirect?: string;
  actionId?: string;
  /** When false/undefined, FE must not offer an auto-send control. */
  sendApproved?: boolean;
  /** Typed copy-only operation — never a send. */
  copyAction?: ActionDirective;
};

export type OutstandingSlice = {
  openCount: number;
  remainingAmountMinor?: number;
  currency?: string;
  items: BacklogChildRow[];
  reminderDrafts?: OutstandingReminderDraft[];
};

export type WorkQueueBucket = {
  id: WorkBucketId;
  label: string;
  count: number;
  cards: LiffFinancialWorkCard[];
};

export type WorkQueueProjection = {
  projectionRevision: number;
  freshness: "current" | "stale" | "projection_pending" | "unavailable";
  actorLabel?: string;
  scopeLabel?: string;
  buckets: WorkQueueBucket[];
  observedAt?: string;
  /** When true, FE must not invent bucket membership — server omitted projection. */
  serverProjectionMissing?: boolean;
  actorCapabilities?: string[];
  navigation?: {
    sourcesAndSharing?: { visible?: boolean };
  };
  /** BillLin receivable totals/status — visible ยอดค้าง must match this. */
  canonicalTruth?: {
    source: "billlin.receivable";
    openCount: number;
    closedCount: number;
    remainingAmountMinor: number;
    paidAmountMinor: number;
    grossAmountMinor: number;
    statusCounts?: {
      open: number;
      partially_paid: number;
      closed: number;
    };
  };
  /** Server-paged feed. When set, the list is one page from /financial-work/feed/{tab}. */
  page?: {
    index: number;
    size: number;
    total: number;
    bucket: string;
    q?: string;
  };
  pageCards?: LiffFinancialWorkCard[];
  /** Totals for every tab. Each feed page returns complete counts, not only the open tab. */
  tabCounts?: {
    needs_action: number;
    waiting: number;
    recently_closed: number;
    all: number;
  };
};

export type FinancialWorkFeedKind = "payment" | "receivable" | "expense";

/** Admin simulate cases that ask the shop on LINE — same cards on ต้องให้คุณทำ. */
export type HumanAskKind =
  | "slip_owner_select"
  | "slip_confirm"
  | "unmatched_slip"
  | "split_suggest"
  | "retro_match";

export const HUMAN_ASK_KINDS: readonly HumanAskKind[] = [
  "slip_owner_select",
  "slip_confirm",
  "unmatched_slip",
  "split_suggest",
  "retro_match",
];

export type FinancialWorkFeedBucket =
  | "needs_action"
  | "waiting"
  | "recently_closed";

/** GET /api/v1/financial-work/feed/{tab} — one page for one tab. */
export type FinancialWorkFeedItem = {
  objectRef: ObjectRef;
  kind: FinancialWorkFeedKind;
  createdAt: string;
  /** Last touch — drawer 「อัปเดตล่าสุด」. Queue 「วันที่」 uses createdAt. */
  updatedAt?: string;
  title: string;
  subtitle: string;
  reasonCodes: string[];
  reasonLabelsTh: string[];
  sourceLabel: string;
  /** Expense only — canonical document_type from expense_records (`receipt` | `invoice`). */
  documentType?: string;
  direction: "in" | "out";
  amountMinor: number;
  /** Incoming money already applied or offered against a ยอด (e.g. slip ฿45,000). */
  amountPaidMinor?: number;
  /** What is left on that ยอด after the incoming money (e.g. ฿5,000). */
  remainingAmountMinor?: number;
  /** Receivable deadline — created_at + announcement TTL, or live Redis expiry. */
  expiresAt?: string;
  /** Same-origin slip photo for human-ask / pending payment rows. */
  imageUrl?: string;
  /** Matched income record_id — closed payment thumbs / open_data must use this, not payment id. */
  incomeRecordId?: string;
  status: string;
  bucket: FinancialWorkFeedBucket;
  candidateId?: string | null;
  lastDecision?: string | null;
  /** Stored income match_method. Queue display is only manual vs auto_amount. */
  matchMethod?: string | null;
  linkedCandidateIds: string[];
  /** Shared receivable batch — only set when 2+ items share it. */
  batchId?: string | null;
  /** Workspace-wide size of that batch, including mates not on this page. */
  batchMemberCount?: number;
  /** Closed members in that batch, including mates not on this page. */
  batchClosedCount?: number;
  /** Full batch roster for detail — only when 2+ members share batchId. */
  batchMembers?: BacklogChildRow[];
  /** LINE ask card from admin simulate that must reach the shop. */
  askKind?: HumanAskKind | null;
  clarifyOptions?: Array<{
    candidateId: string;
    confirmId?: string;
    label: string;
    partyName: string;
    amountMinor: number;
    receivableRevision?: number;
    receivableId?: string;
    orderId?: string;
    createdAt?: string;
    expiresAt?: string;
  }>;
  recommendation?: {
    headline: string;
    detail?: string;
  } | null;
  /** FIFO unequal-sum split: evidence comparison blurb under หลักฐาน. */
  comparisonNote?: string | null;
  splitMode?: "exact" | "fifo" | string | null;
  availableActions?: ActionDirective[];
};

/** GET /api/v1/financial-work/feed/{tab} — ต้องให้คุณทำ / กำลังถือ / ปิดล่าสุด / ทั้งหมด. */
export type FinancialWorkFeedBucketGroup = {
  id: FinancialWorkFeedBucket | "all";
  label: string;
  count: number;
  items: FinancialWorkFeedItem[];
};

export type FinancialWorkFeed = {
  workspaceId?: string;
  observedAt?: string;
  items: FinancialWorkFeedItem[];
  buckets: FinancialWorkFeedBucketGroup[];
  counts: {
    needs_action: number;
    waiting: number;
    recently_closed: number;
    all: number;
  };
  page?: number;
  pageSize?: number;
  offset?: number;
  total?: number;
  bucket?: FinancialWorkFeedBucket | "all";
  q?: string;
  actorCapabilities?: string[];
  navigation?: {
    sourcesAndSharing?: { visible?: boolean };
  };
};

/**
 * LINE-00 compact Financial Work card — server-authored only.
 * Payload rules: opaque actionId + object/revision; no private evidence bytes or account ids.
 */
export type LineFinancialWorkCard = {
  objectRef: ObjectRef;
  caseRef?: ObjectRef;
  projectionRevision: number;
  audience: "person" | "scope_member" | "external_recipient" | "accountant";
  title: string;
  receiptLines: string[];
  amountSummary?: {
    currency: string;
    primaryAmountMinor: number;
    basis: "cash" | "gross" | "remaining" | "due" | "aggregate";
  };
  workPlacement: WorkPlacementV1;
  requirementStatus: RequirementStatus;
  nextStep: NextStep;
  primaryAction?: ActionDirective;
  secondaryAction?: ActionDirective;
  detailLink: string;
  issuedAt: string;
  freshness: "current" | "stale" | "projection_pending";
};

export type FinancialWorkNavId = "work" | "data" | "sources" | "periods";

export type FinancialWorkIntent = {
  v: 1;
  objectRef?: ObjectRef;
  caseRef?: ObjectRef;
  desiredAction?: string;
  nav?: FinancialWorkNavId;
  returnPath: string;
  savedAt: string;
};

export type OperationRecoveryState =
  | { kind: "idle" }
  | { kind: "submitting"; operationKey: string }
  | { kind: "processing"; operationKey: string; message?: string }
  | {
      kind: "committed";
      operationKey: string;
      status: string;
      after?: Record<string, unknown>;
    }
  | {
      kind: "failed";
      operationKey: string;
      message: string;
      retrySafeWithSameKey: boolean;
    };
